import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, suspendUser, activateUser } from '../../services/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [filters, pagination.page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await getUsers(params);
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (err) {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleUserAction = async (userId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      if (action === 'suspend') {
        await suspendUser(userId);
      } else if (action === 'activate') {
        await activateUser(userId);
      }
      
      fetchUsers();
    } catch (err) {
      setError(`Failed to ${action} user. Please try again.`);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (user) => {
    if (user.deleted_at) {
      return (
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-400 border border-red-700/50">
          <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-1.5"></div>
          Suspended
        </div>
      );
    }
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></div>
        Active
      </div>
    );
  };

  const getRoleBadge = (user) => {
    if (user.is_seller) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-900/30 to-indigo-900/30 text-blue-400 border border-blue-700/50">
          <span className="mr-1.5">🏪</span>
          Seller
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
        <span className="mr-1.5">🛒</span>
        Buyer
      </span>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-6 space-y-4 md:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-white">
                User <span className="text-[#ff660e]">Management</span>
              </h1>
              <p className="text-gray-400 mt-1">Manage all users and their accounts</p>
            </div>
            <button
              onClick={fetchUsers}
              className="bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg px-4 py-2 text-white transition-all duration-200 flex items-center space-x-2 hover:scale-[1.02]"
            >
              <span>🔄</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button 
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-3 text-[#ff660e] text-xl">🔍</span>
            Filters
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
              />
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
              >
                <option value="">All Roles</option>
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={fetchUsers}
                className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-750 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {user.profile_picture_url ? (
                            <img
                              className="h-12 w-12 rounded-full ring-2 ring-gray-700"
                              src={user.profile_picture_url}
                              alt={user.first_name}
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-full flex items-center justify-center text-white font-semibold text-lg ring-2 ring-gray-700">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-white">
                            {user.first_name} {user.middle_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-400">{user.email}</div>
                          {user.phone_number && (
                            <div className="text-sm text-gray-500">{user.phone_number}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                      <button
                        onClick={() => navigate(`/admin/users/${user.user_id}`)}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-medium flex items-center space-x-1 hover:scale-105 transition-transform"
                      >
                        <span>👁️</span>
                        <span>View</span>
                      </button>
                      {user.deleted_at ? (
                        <button
                          onClick={() => handleUserAction(user.user_id, 'activate')}
                          className="text-green-400 hover:text-green-300 transition-colors font-medium flex items-center space-x-1 hover:scale-105 transition-transform"
                        >
                          <span>✅</span>
                          <span>Activate</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUserAction(user.user_id, 'suspend')}
                          className="text-red-400 hover:text-red-300 transition-colors font-medium flex items-center space-x-1 hover:scale-105 transition-transform"
                        >
                          <span>⏸️</span>
                          <span>Suspend</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {users.length === 0 && !loading && (
            <div className="text-center py-16 border-t border-gray-700">
              <div className="text-gray-400 text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No users found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-gray-800 px-6 py-4 border-t border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="text-sm text-gray-400">
                  Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                  <span className="font-semibold text-white">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-semibold text-white">{pagination.total}</span> users
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 hover:border-[#ff660e] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-700 disabled:hover:border-gray-600 flex items-center space-x-2"
                  >
                    <span>←</span>
                    <span>Previous</span>
                  </button>
                  <div className="px-3 py-2 text-sm text-gray-400">
                    Page <span className="font-semibold text-white">{pagination.page}</span> of{' '}
                    <span className="font-semibold text-white">{pagination.pages}</span>
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 hover:border-[#ff660e] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-700 disabled:hover:border-gray-600 flex items-center space-x-2"
                  >
                    <span>Next</span>
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Total Users</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {pagination.total}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Active Users</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {users.filter(u => !u.deleted_at).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-500 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Sellers</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {users.filter(u => u.is_seller).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">🏪</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;